import { Circle, Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';

/**
 * Perimeter seat — circular avatar disc + inward-pointing triangle
 * indicator + tap target for SELECT_SEAT during STAKE.
 *
 * <p>Producer brief 2026-05-16 — the wheel-resolve view is being
 * lifted out of React/DOM into Pixi. This widget replaces the
 * legacy {@code SeatPointer} React component. Visual recipe is
 * preserved 1:1 so post-port the player can't tell the canvas
 * changed:
 * <ul>
 *   <li>Gold ring + golden glow on the local player's seat (isSelf)</li>
 *   <li>Inward-pointing triangle below the avatar, rotated to point
 *       at the wheel hub regardless of where the seat sits</li>
 *   <li>Dashed pulse around the disc when tappable (STAKE for
 *       non-self seats)</li>
 * </ul>
 *
 * <p>The avatar sprite is provided by the host scene — the engine
 * doesn't know about React's avatar registry, so the host hands in
 * a Texture per seat (mapped via the player profile + bot archetype).
 */

const FONT_FAMILY = '"Fredoka", system-ui, -apple-system, sans-serif';

export interface WheelResolveSeatPointerOptions {
  diameter: number;
  /** Optional gold pointer texture rendered ONLY on the local
   *  player's seat (when {@code state.isSelf} is true). The sprite
   *  is rotated so its tip points at the wheel centre. */
  pointerTexture?: Texture | null;
  /** Optional high-five texture for the per-seat gesture badge
   *  (producer 2026-06-08 — "add the same highfive to the wheel game
   *  avatars"). When supplied, a tappable badge is pinned at the
   *  avatar's lower-left; the host toggles it via
   *  {@link WheelResolveSeatPointerState.showGesture} and wires the
   *  tap via {@link setOnGesture}. Omit to disable the affordance. */
  highFiveTexture?: Texture | null;
}

export interface WheelResolveSeatPointerState {
  /** Texture for the avatar disc. null → render an initial-letter
   *  fallback (mirrors the legacy React SeatAvatar fallback). */
  avatarTexture: Texture | null;
  /** Player display name — used for the fallback initial when no
   *  avatar texture is provided. */
  displayName: string;
  /** Pointer slot angle in degrees (0 = top of wheel, clockwise). */
  pointerAngleDeg: number;
  isSelf: boolean;
  /** When true, the seat receives pointer taps and shows the
   *  pointer cursor. The host wires the callback via
   *  {@link setOnTap}. False makes taps fall through to wedge-area
   *  taps. Decoupled from {@link attractSwap} so a seat can be tappable
   *  for "open profile card" during WHEEL_RESOLVE without advertising
   *  itself as a swap target. */
  interactive: boolean;
  /** When true, the seat draws the dashed yellow pulse-ring that
   *  reads as "tap me to swap into this slot". STAKE-only by
   *  convention — the U11 ProfileCard tap during WHEEL_RESOLVE keeps
   *  the seat tappable but does NOT show the swap attract. */
  attractSwap: boolean;
  /** When true, show the tappable high-five badge at the avatar's
   *  lower-left. Host gates this to non-self occupants during the
   *  spectating phases (WHEEL_REVEAL / WHEEL_RESOLVE). Optional —
   *  treated as false when omitted (and a no-op unless the widget
   *  was built with a {@code highFiveTexture}). */
  showGesture?: boolean;
}

export class WheelResolveSeatPointer extends Container {
  private readonly avatarHost: Container;
  private readonly avatarMask: Graphics;
  private readonly avatarBg: Graphics;
  private readonly avatarRing: Graphics;
  private readonly pulseRing: Graphics;
  private readonly pointerTri: Graphics;
  private readonly tapTarget: Graphics;
  /** Producer brief 2026-05-16 — when a slot is unoccupied during
   *  STAKE, render a dashed-ring "+" marker so the player has a
   *  clear affordance to claim the seat. The marker hides when the
   *  seat is occupied. */
  private readonly emptyMarker: Container;
  private readonly emptyRing: Graphics;
  private readonly emptyPlus: Text;
  /** Self-seat pointer arrow — rotates so its tip points inward
   *  at the wheel hub. {@link pointerSprite} hidden for non-self
   *  seats. The host Container's rotation maps its local +y to
   *  the seat→hub direction so the sprite can be laid out in a
   *  consistent local frame regardless of which slot the seat
   *  ends up at. */
  private readonly pointerHost: Container;
  private pointerSprite: Sprite | null = null;
  private avatarSprite: Sprite | null = null;
  private fallbackText: Text | null = null;
  /** High-five gesture badge (lower-left). Built only when a
   *  {@code highFiveTexture} is supplied; null otherwise. */
  private gestureBadge: Container | null = null;
  private gestureBadgeBg: Graphics | null = null;
  private gestureSprite: Sprite | null = null;
  private onGesture: (() => void) | null = null;
  private readonly opts: WheelResolveSeatPointerOptions;
  private currentState: WheelResolveSeatPointerState | null = null;
  private onTap: (() => void) | null = null;
  private pulseElapsed = 0;
  private emptyVisible = false;

  constructor(options: WheelResolveSeatPointerOptions) {
    super();
    this.opts = options;
    const d = options.diameter;

    // Pulse ring (tappable affordance) — dashed look approximated
    // via a thin stroked circle that fades in/out.
    this.pulseRing = new Graphics();
    this.pulseRing
      .circle(0, 0, d / 2 + 4)
      .stroke({ color: 0xffe48a, alpha: 0.7, width: 2 });
    this.pulseRing.visible = false;
    this.addChild(this.pulseRing);

    // Inward-pointing triangle below the avatar.
    this.pointerTri = new Graphics();
    this.addChild(this.pointerTri);

    // Avatar disc — solid bg + masked sprite + ring overlay.
    this.avatarBg = new Graphics();
    this.avatarBg.circle(0, 0, d / 2).fill({ color: 0x0b1838, alpha: 1 });
    this.addChild(this.avatarBg);

    this.avatarHost = new Container();
    this.addChild(this.avatarHost);
    this.avatarMask = new Graphics();
    this.avatarMask.circle(0, 0, d / 2).fill({ color: 0xffffff });
    this.avatarHost.addChild(this.avatarMask);
    this.avatarHost.mask = this.avatarMask;

    this.avatarRing = new Graphics();
    this.addChild(this.avatarRing);

    // Invisible hit target — extended slightly beyond the avatar
    // for tap forgiveness on mobile.
    this.tapTarget = new Graphics();
    const hitR = d / 2 + d * 0.18;
    this.tapTarget
      .circle(0, 0, hitR)
      .fill({ color: 0xffffff, alpha: 0.001 });
    this.tapTarget.eventMode = 'static';
    this.tapTarget.cursor = 'default';
    this.tapTarget.on('pointertap', (event) => {
      event.stopPropagation();
      if (this.onTap) this.onTap();
    });
    this.addChild(this.tapTarget);

    // Empty-seat marker — dashed gold ring + "+" glyph for
    // unoccupied STAKE slots. Drives the seat-pick affordance for
    // the local player.
    this.emptyMarker = new Container();
    this.emptyRing = new Graphics();
    this.emptyMarker.addChild(this.emptyRing);
    this.emptyPlus = new Text({
      text: '＋',
      style: {
        fontFamily: '"Fredoka", system-ui',
        fontSize: 18,
        fontWeight: '900',
        fill: 0xffe48a,
      },
    });
    this.emptyPlus.anchor.set(0.5);
    this.emptyMarker.addChild(this.emptyPlus);
    this.emptyMarker.visible = false;
    this.addChild(this.emptyMarker);

    // Self-seat pointer arrow. Producer feedback 2026-05-17 — the
    // legacy `pointerTri` graphic stays for layout compatibility
    // (its draw is cleared in setState below), but the actual
    // visible pointer is now an asset sprite rendered via
    // {@link pointerHost}. The host's angle is set so its local
    // +y points at the wheel hub, regardless of which slot this
    // seat sits at, and the sprite's tip (image bottom) sits at
    // the avatar's outer edge so the arrow LEADS the avatar
    // toward the wheel.
    this.pointerHost = new Container();
    this.pointerHost.visible = false;
    this.addChild(this.pointerHost);
    if (options.pointerTexture) {
      const sprite = new Sprite(options.pointerTexture);
      sprite.anchor.set(0.5, 1);
      const target = Math.round(options.diameter * 0.7);
      sprite.width = target;
      sprite.height = target;
      this.pointerHost.addChild(sprite);
      this.pointerSprite = sprite;
    }

    // High-five gesture badge — producer 2026-06-08 ("add the same
    // highfive to the wheel game avatars"). A small navy disc with the
    // high-five sprite, pinned at the avatar's lower-left, mirroring the
    // DOM SeatGestureButton. Tapping it sends a directed high-five at
    // this seat (separate from the swap/profile tap on the avatar
    // itself). Added LAST so it sits above the avatar disc + tapTarget
    // and wins the hit-test in its corner. Hidden until the host turns
    // it on via {@link WheelResolveSeatPointerState.showGesture}.
    if (options.highFiveTexture) {
      const badge = new Container();
      badge.eventMode = 'static';
      badge.cursor = 'pointer';
      badge.on('pointertap', (event) => {
        event.stopPropagation();
        if (this.onGesture) this.onGesture();
      });
      const bg = new Graphics();
      badge.addChild(bg);
      const spr = new Sprite(options.highFiveTexture);
      spr.anchor.set(0.5);
      spr.eventMode = 'none';
      badge.addChild(spr);
      badge.visible = false;
      this.gestureBadge = badge;
      this.gestureBadgeBg = bg;
      this.gestureSprite = spr;
      this.addChild(badge);
      this.drawGestureBadge(d);
    }
  }

  /** Position + size the high-five badge relative to the current
   *  avatar diameter. Sits at the avatar's lower-left, half-overlapping
   *  the disc edge, mirroring the DOM SeatGestureButton's corner pin. */
  private drawGestureBadge(d: number): void {
    if (!this.gestureBadge || !this.gestureBadgeBg) return;
    const badgeR = Math.round(d * 0.25);
    // Lower-left of the disc (screen y is down → positive y).
    this.gestureBadge.position.set(-d * 0.38, d * 0.38);
    this.gestureBadge.hitArea = new Circle(0, 0, badgeR);
    this.gestureBadgeBg
      .clear()
      .circle(0, 0, badgeR)
      .fill({ color: 0x0b1838, alpha: 0.92 })
      .stroke({ color: 0xa8e0ff, alpha: 0.85, width: 1.5 });
    if (this.gestureSprite) {
      const inner = Math.round(badgeR * 2 * 0.72);
      this.gestureSprite.width = inner;
      this.gestureSprite.height = inner;
    }
  }

  /** Producer brief 2026-05-16 — render an empty-slot marker
   *  (dashed gold ring + "+" glyph) when the seat is unoccupied
   *  during STAKE. Pass {@code false} to hide it. */
  setEmptyMarker(visible: boolean): void {
    if (visible === this.emptyVisible) return;
    this.emptyVisible = visible;
    this.emptyMarker.visible = visible;
    if (visible) {
      const d = this.opts.diameter;
      this.emptyRing.clear();
      this.emptyRing
        .circle(0, 0, d / 2)
        .stroke({ color: 0xffe48a, alpha: 0.85, width: 3 });
      this.emptyPlus.style.fontSize = Math.round(d * 0.42);
    }
  }

  /** Drop the avatar + ring + self-pointer sprite for a slot that
   *  just emptied. Producer feedback 2026-05-27 — when the player
   *  SELECT_SEAT-swapped to a new slot, the old slot still painted
   *  the player avatar under the empty-marker, so the player looked
   *  like they were sitting in two places. Invalidates currentState
   *  so the next {@link setState} will rebuild from scratch. */
  clearOccupant(): void {
    if (this.avatarSprite) {
      this.avatarSprite.destroy();
      this.avatarSprite = null;
    }
    if (this.fallbackText) {
      this.fallbackText.destroy();
      this.fallbackText = null;
    }
    this.avatarRing.clear();
    this.pointerHost.visible = false;
    if (this.gestureBadge) this.gestureBadge.visible = false;
    this.currentState = null;
  }

  /** Resize the avatar disc + pulse ring + tap area. The host
   *  scene calls this on every resize so the seat scales with the
   *  wheel tier without container scaling blowing up text. */
  setDiameter(diameter: number): void {
    if (diameter === this.opts.diameter) return;
    this.opts.diameter = diameter;
    const d = diameter;
    this.avatarBg.clear();
    this.avatarBg.circle(0, 0, d / 2).fill({ color: 0x0b1838, alpha: 1 });
    this.avatarMask.clear();
    this.avatarMask.circle(0, 0, d / 2).fill({ color: 0xffffff });
    this.pulseRing.clear();
    this.pulseRing
      .circle(0, 0, d / 2 + 4)
      .stroke({ color: 0xffe48a, alpha: 0.7, width: 2 });
    this.tapTarget.clear();
    const hitR = d / 2 + d * 0.18;
    this.tapTarget
      .circle(0, 0, hitR)
      .fill({ color: 0xffffff, alpha: 0.001 });
    if (this.avatarSprite) {
      this.avatarSprite.width = d;
      this.avatarSprite.height = d;
    }
    if (this.pointerSprite) {
      const target = Math.round(d * 0.7);
      this.pointerSprite.width = target;
      this.pointerSprite.height = target;
    }
    this.drawGestureBadge(d);
    // Force-rerender on next setState (state cache invalidated).
    this.currentState = null;
  }

  /** Update the seat's visual + position. The host scene computes
   *  (cx, cy) from the pointer angle on the orbit and calls this
   *  on every state change. */
  setState(
    cx: number,
    cy: number,
    state: WheelResolveSeatPointerState,
  ): void {
    this.x = cx;
    this.y = cy;
    const stateChanged =
      this.currentState === null ||
      this.currentState.avatarTexture !== state.avatarTexture ||
      this.currentState.displayName !== state.displayName ||
      this.currentState.pointerAngleDeg !== state.pointerAngleDeg ||
      this.currentState.isSelf !== state.isSelf ||
      this.currentState.interactive !== state.interactive ||
      this.currentState.attractSwap !== state.attractSwap;
    this.currentState = state;
    // Gesture badge visibility — cheap to toggle every tick, so set it
    // before the stateChanged early-out (a phase flip that only flips
    // showGesture shouldn't force a full avatar rebuild). No-op when the
    // widget was built without a high-five texture.
    if (this.gestureBadge) {
      this.gestureBadge.visible = (state.showGesture ?? false) && !state.isSelf;
    }
    if (!stateChanged) return;

    // Avatar sprite — refresh texture or fall back to initial.
    if (this.avatarSprite) {
      this.avatarSprite.destroy();
      this.avatarSprite = null;
    }
    if (this.fallbackText) {
      this.fallbackText.destroy();
      this.fallbackText = null;
    }
    if (state.avatarTexture) {
      const sprite = new Sprite(state.avatarTexture);
      sprite.anchor.set(0.5);
      sprite.width = this.opts.diameter;
      sprite.height = this.opts.diameter;
      this.avatarHost.addChild(sprite);
      this.avatarSprite = sprite;
    } else {
      const initial = (state.displayName || '?').slice(0, 1).toUpperCase();
      const text = new Text({
        text: initial,
        style: {
          fontFamily: FONT_FAMILY,
          fontSize: Math.round(this.opts.diameter * 0.5),
          fontWeight: '900',
          fill: 0xffe9a6,
        },
      });
      text.anchor.set(0.5);
      this.avatarHost.addChild(text);
      this.fallbackText = text;
    }

    // Ring overlay — gold halo for self, dark border for others.
    this.avatarRing.clear();
    if (state.isSelf) {
      this.avatarRing
        .circle(0, 0, this.opts.diameter / 2 + 1)
        .stroke({ color: 0xffc940, alpha: 1, width: 2 })
        .circle(0, 0, this.opts.diameter / 2 + 4)
        .stroke({ color: 0xffc940, alpha: 0.5, width: 4 });
    } else {
      this.avatarRing
        .circle(0, 0, this.opts.diameter / 2 + 1)
        .stroke({ color: 0x060d22, alpha: 1, width: 2 });
    }

    // Legacy inward triangle retired in favour of the gold pointer
    // sprite. Clear so nothing draws — keeping the field avoids a
    // ripple of test/preview-fixture changes.
    this.pointerTri.clear();

    // Self-seat pointer arrow. The pointerHost container rotates so
    // that its local +y axis maps to the seat→hub direction in screen.
    //
    // Rotation derivation (producer feedback 2026-05-17, fixed sign):
    //   For pointerAngleDeg = α (CW from top), seat is at screen
    //   position (sin α, -cos α) × orbit; toward-hub direction is the
    //   negative of that = (-sin α, +cos α). Pixi's rotation by angle
    //   θ maps local (0, 1) → (-sin θ, cos θ) in parent frame, so set
    //   θ = α. (The previous -α was wrong; that aligned local +y with
    //   the OPPOSITE of toward-hub, which is why pointers on the
    //   upper-right seats pointed outward to the lower-right.)
    //
    // Position: anchor at image bottom-centre = TIP. Place the tip
    // INSIDE the avatar's hub-side edge so the arrow body sits in the
    // dark wedge area (BETWEEN avatar and hub) and stops obscuring the
    // avatar's face. Producer feedback 2026-05-17 — "moved further
    // towards the wheel centre".
    if (this.pointerSprite) {
      const showPointer = state.isSelf;
      this.pointerHost.visible = showPointer;
      if (showPointer) {
        this.pointerHost.angle = state.pointerAngleDeg;
        // sprite height ≈ diameter * 0.7 (set in constructor /
        // setDiameter). Push the tip past the avatar's inner edge by
        // a full image height so the body sits inside the rim band,
        // not on the avatar's face.
        const imgH = Math.round(this.opts.diameter * 0.7);
        this.pointerSprite.y = this.opts.diameter / 2 + imgH;
      }
    }

    // Pulse ring advertises "tap-to-swap" — STAKE-only via the
    // {@link attractSwap} flag. The U11 profile-tap during
    // WHEEL_RESOLVE keeps the seat tappable (interactive=true) but
    // skips the pulse so the spin moment isn't visually busy.
    this.pulseRing.visible = state.attractSwap && !state.isSelf;
    this.tapTarget.cursor =
      state.interactive && !state.isSelf ? 'pointer' : 'default';
    this.tapTarget.eventMode = state.interactive ? 'static' : 'none';
  }

  /** Host-supplied tap handler. The seat fires this on
   *  {@code pointertap} when {@code interactive} is true and the
   *  seat isn't the local self (taps on your own seat are no-ops). */
  setOnTap(handler: (() => void) | null): void {
    this.onTap = handler;
  }

  /** Host-supplied high-five handler. Fires on a {@code pointertap} of
   *  the gesture badge, independent of {@link setOnTap} (which stays the
   *  swap / profile affordance on the avatar disc). */
  setOnGesture(handler: (() => void) | null): void {
    this.onGesture = handler;
  }

  /** Drive the pulse-ring fade so the dashed affordance reads as
   *  alive. Host scene's ticker calls this every frame. */
  tick(deltaMs: number): void {
    if (!this.pulseRing.visible) return;
    this.pulseElapsed += deltaMs;
    const cycle = 1500;
    const t = (this.pulseElapsed % cycle) / cycle;
    // Mirror the legacy 0..100% keyframe: opacity 0.45 → 1 → 0.45,
    // scale 0.95 → 1.05 → 0.95.
    const pulse = Math.sin(t * Math.PI * 2);
    this.pulseRing.alpha = 0.7 + pulse * 0.3;
    this.pulseRing.scale.set(1 + pulse * 0.05);
  }

  destroyDisplay(): void {
    this.removeChildren();
  }
}
